import { BaseWidgetView } from '../../widget/BaseWidgetView';
import bb from 'billboard.js/dist/billboard.pkgd';
import { htmlDecode } from '../../utils/escape.utils';
import {
  EndpointModuleStatus,
  GetEndpointModuleStatusResponse,
  Status,
} from '@nirvana/shared/dtos/types/widgets/common';
import { TemplateState } from '../../types/common/types';
import { BackendResponseWithMeta, DASHBOARD_METADATA_KEY } from '../../types/widget/types';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { ModuleStatusParams, NavigationHelper, NavigationItem } from '@nirvana/shared/navigation';
import { exitFullscreen, isBrowserFullscreen, onExitFullscreenError } from '../../utils/fullscreen.utils';

export class EndpointModulesView extends BaseWidgetView {
  public shadow: any;
  protected translations: any;
  private $chart: any;
  private readonly $chartLegend: HTMLElement;
  private readonly $chartTooltip: HTMLElement;
  private readonly $style: any;
  private colors: {
    enabled: string;
    disabled: string;
    notInstalled: string;
  };
  private errorColor: string;
  private dataFields: Array<[string, string]>;
  private basicChartOptions: any;
  private navigationHandlers: Array<(event: any) => void> = [];

  private setChartColors() {
    const enabledColor = this.dom.$style.getPropertyValue('--green-teal-chart-400').trimLeft();
    const disabledColor = this.dom.$style.getPropertyValue('--orange-alert-chart-100').trimLeft();
    const notInstalledColor = this.dom.$style.getPropertyValue('--teal-chart-050').trimLeft();

    this.colors = {
      enabled: enabledColor,
      disabled: disabledColor,
      notInstalled: notInstalledColor,
    };
    this.errorColor = this.dom.$style.getPropertyValue('--grey-layout-203-dark-theme-disabled-text').trimLeft();
  }

  private setBasicChartOptions() {
    this.basicChartOptions = {
      bar: {
        width: {
          max: 60,
        },
      },
      axis: {
        x: {
          type: 'category',
          height: 50,
          tick: {
            width: 100,
          },
        },
        y: {
          max: 100,
          padding: {
            top: 0,
          },
          tick: {
            count: 5,
          },
        },
      },
      size: {
        height: 160,
      },
      legend: {
        contents: {
          bindto: this.dom.$chartLegend,
          template: `<div class='gz-data-item'>
                    <span class='gz-data-item--bullet' style=background-color:{=COLOR}></span>
                    <div class="gz-data-item--title">{=TITLE}</div>
                </div>`,
        },
      },
      grid: {
        y: {
          show: true,
        },
      },
      bindto: this.dom.$chartContainer,
    };
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getEndpointModulesWidgetStrings();
  }

  setTranslatedStrings() {
    const { title, options } = this.translations;
    this.dom.$widgetTitleString.innerHTML = title;
    this.dataFields = [
      ['enabled', options.enabled],
      ['disabled', options.disabled],
      ['notInstalled', options.notInstalled],
    ];
  }

  getDomElements() {
    super.getDomElements();
    this.dom.$widgetTitle = this.shadow.querySelector('.widget-title');
    this.dom.$widgetTitleString = this.shadow.querySelector('#endpointModulesTitle');
    this.dom.$chartContainer = this.shadow.querySelector('#endpointModulesChart');
    this.dom.$chartLegend = this.shadow.querySelector('#endpointModulesChartLegend');
    this.dom.$chartTooltip = this.shadow.querySelector('#endpointModulesChartTooltip');
    this.dom.$style = getComputedStyle(document.body);
    this.getTranslations();
    this.setChartColors();
    this.setTranslatedStrings();
    this.setBasicChartOptions();
    this.setErrorTooltip();
  }

  private getErrorChartOptions(chartSeries) {
    return {
      ...this.basicChartOptions,
      data: {
        columns: [...chartSeries.columns],
        type: 'bar',
        color: (color, d) => {
          return this.errorColor;
        },
        groups: [chartSeries.groups],
      },
      tooltip: {
        show: false,
      },
      interaction: {
        enabled: false,
      },
      axis: {
        x: {
          type: 'category',
          tick: {
            format(x) {
              return '--';
            },
          },
        },
        y: {
          tick: {
            stepSize: 25,
          },
        },
      },
    };
  }

  buildSuccessView(response: BackendResponseWithMeta<GetEndpointModuleStatusResponse>) {
    const chartActiveClass = 'widget-chart--active';

    this.resetTemplateState(this.$wrapper);
    this.removeElementClass(this.dom.$chartContainer, chartActiveClass);

    const result = response.widgetResult;

    if (result.status === Status.EMPTY_STATE) {
      this.setTemplateState(this.$wrapper, TemplateState.EMPTY);
      this.buildEmptyTemplate();
      return;
    }

    if (result.data && result.status === Status.ACTIVE) {
      const formattedData = this.formatEndpointModulesNames(result.data);
      this.buildChart(formattedData);
      this.setElementClass(this.dom.$chartContainer, chartActiveClass);

      if (!result.networkNodeId) {
        //This is a programmatic error, it should never happen
        throw new Error('Unable to get target for drilling down into endpoint module status');
      }
      this.addDrillDownHandlers(
        response[DASHBOARD_METADATA_KEY].targets,
        result.networkNodeId,
        response[DASHBOARD_METADATA_KEY].canDrillDown
      );
      return;
    }

    this.buildErrorView();
  }

  buildErrorView() {
    this.resetTemplateState(this.$wrapper);
    this.setTemplateState(this.$wrapper, TemplateState.ERROR);
    this.buildEmptyTemplate();
  }

  private buildEmptyTemplate() {
    const groups = this.dataFields.map(([key, value]) => value);
    const { options } = this.translations;

    const chartSeries = {
      columns: [
        [options.enabled, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        [options.disabled, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        [options.notInstalled, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100],
      ],
      groups,
    };

    /**
     * we need to destroy the chart, then generate it again if the options
     * are changed from one state to another
     * https://github.com/naver/billboard.js/issues/424
     */
    if (this.$chart) {
      this.$chart.destroy();
    }

    this.$chart = bb.generate(this.getErrorChartOptions(chartSeries));

    //legend is disabled by css
    this.dom.$chartLegend.classList.add('disabled-field-error');
  }

  private buildChart(data: Array<EndpointModuleStatus>) {
    const columns = this.formatColumns(data);

    //We need groups and column ids to have the same order
    const groups = this.dataFields.map(([key, value]) => value);

    const labels = this.getLabels(data);
    const colors = this.formatColors();

    const chartSeries = {
      columns,
      groups,
      colors,
      labels,
    };

    if (this.$chart) {
      this.$chart.destroy();
    }

    const chartOptions = this.getBasicChartOptions(chartSeries);
    //Setting the maximum height of the chart Oy ticks
    chartOptions.axis.y.max = data[0].enabled + data[0].disabled + data[0].notInstalled;
    this.$chart = bb.generate(chartOptions);
  }

  private getBasicChartOptions(chartSeries) {
    return {
      ...this.basicChartOptions,
      data: {
        x: 'x',
        order: '',
        columns: [['x', ...chartSeries.labels], ...chartSeries.columns],
        type: 'bar',
        colors: chartSeries.colors,
        groups: [chartSeries.groups],
        labels: false,
      },
      tooltip: {
        format: {
          value: value => `${value}`,
        },
        contents: {
          bindTo: this.dom.$chartTooltip,
          template: `<div class="chart-tooltip-container"><div class="chart-tooltip-title">{=TITLE}</div>
                {{<div class='gz-data-item'>
                    <span class='gz-data-item--bullet' style=background-color:{=COLOR}></span>
                    <div class="gz-data-item--title">{=NAME}</div>
                    <div class="gz-data-item--value">{=VALUE}</div>
                </div>}}</div>`,
        },
      },
    };
  }

  private getLabels(data: Array<EndpointModuleStatus>) {
    return data.map(endpointModuleStatus => endpointModuleStatus.name);
  }

  private formatColumns(data: Array<EndpointModuleStatus>) {
    return this.dataFields.map(([key, value]) => {
      return [value, ...this.formatColumnByKey(data, key)];
    });
  }

  private formatColumnByKey(data: Array<EndpointModuleStatus>, key: string): Array<any> {
    return data.map((element: EndpointModuleStatus) => {
      return element[key];
    });
  }

  private formatColors() {
    return this.dataFields.reduce((accumulator: Object, [key, value]) => {
      return {
        ...accumulator,
        [value]: this.colors[key],
      };
    }, {});
  }

  private formatEndpointModulesNames(data: Array<EndpointModuleStatus>): Array<EndpointModuleStatus> {
    return data.map((element: EndpointModuleStatus) => {
      return {
        name: htmlDecode(element.name),
        enabled: element.enabled,
        disabled: element.disabled,
        notInstalled: element.notInstalled,
      };
    });
  }

  private addDrillDownHandlers(targets: Array<Target>, networkNodeId: string, canDrillDown: boolean) {
    if (!NavigationHelper.shouldAddHandlers(this.dependencies.Ext)) {
      return;
    }

    const navigationItems: Array<NavigationItem> = [
      {
        elements: [this.dom.$widgetTitle],
        params: {
          title: this.dependencies.translateFn('Endpoint Modules Status'),
          targetData: {
            //For now we can only select one target in Endpoint Modules Status
            companyId: targets[0].companyId,
            networkNodeId: networkNodeId,
          },
        },
      },
    ];

    let navigationHandler;
    if (canDrillDown) {
      navigationHandler = (event, navigationParams: ModuleStatusParams) => {
        if (BaseWidgetView.isSuccessState(this.$wrapper)) {
          if (isBrowserFullscreen()) {
            exitFullscreen(() => {}, onExitFullscreenError.bind(this));
          }

          NavigationHelper.navigateToEndpointModulesStatus(this.dependencies.Ext, navigationParams);
        }
      };
    }

    NavigationHelper.updateNavigableItems(navigationItems, this.navigationHandlers, navigationHandler);
  }
}
